import React from "react";

const raleway = { fontFamily: "'Geist', sans-serif" };
const geistMono = { fontFamily: "'Geist Mono', monospace" };
const instrumentSerif = { fontFamily: "'Instrument Serif', Georgia, serif" };

const CONTACT_TILES = [
  {
    eyebrow: "24/7 FRAUD HOTLINE",
    value: "0800 FRAUD 24",
    href: "tel:0800372834",
    highlight: true,
  },
  {
    eyebrow: "EMERGENCY LINE",
    value: "+234 901 000 0000",
    href: "tel:+2349010000000",
  },
  {
    eyebrow: "WHATSAPP",
    value: "+234 901 234 5678",
    href: "https://wa.me/2349012345678",
  },
  {
    eyebrow: "EMAIL",
    value: "fraud@zikoramfb.com",
    href: "mailto:fraud@zikoramfb.com",
  },
];

const ContactTile = ({ eyebrow, value, href, highlight }) => (
  <a
    href={href}
    target={href?.startsWith("http") ? "_blank" : undefined}
    rel={href?.startsWith("http") ? "noopener noreferrer" : undefined}
    className="fraud-tile"
    style={{
      display: "flex",
      flexDirection: "column",
      gap: "8px",
      padding: "clamp(16px, 1.4vw, 22px)",
      background: highlight ? "#4A2618" : "rgba(255,255,255,0.04)",
      border: highlight
        ? "1px solid rgba(244,210,168,0.18)"
        : "1px solid rgba(255,255,255,0.08)",
      borderRadius: "12px",
      textDecoration: "none",
      color: "inherit",
      boxSizing: "border-box",
      minHeight: "100px",
      transition: "background 200ms ease, border-color 200ms ease",
    }}
  >
    <span
      style={{
        ...geistMono,
        fontWeight: 500,
        fontSize: "10px",
        letterSpacing: "0.1em",
        color: "rgba(255,255,255,0.55)",
        textTransform: "uppercase",
      }}
    >
      {eyebrow}
    </span>
    <span
      style={{
        ...instrumentSerif,
        fontWeight: 400,
        fontSize: "clamp(18px, 1.6vw, 22px)",
        lineHeight: 1.2,
        color: "#FFFFFF",
        wordBreak: "break-word",
      }}
    >
      {value}
    </span>
  </a>
);

const FraudReport = () => {
  return (
    <>
      <style>{`
				.fraud-grid {
					display: flex;
					gap: clamp(28px, 3vw, 56px);
					align-items: stretch;
				}
				.fraud-left { flex: 1 1 55%; min-width: 0; }
				.fraud-right { flex: 1 1 45%; min-width: 0; }
				.fraud-tiles {
					display: grid;
					grid-template-columns: 1fr 1fr;
					gap: clamp(12px, 1.1vw, 18px);
					height: 100%;
				}
				.fraud-tile:hover {
					background: rgba(255,255,255,0.07) !important;
					border-color: rgba(255,255,255,0.18) !important;
				}
				.fraud-tile-highlight:hover {
					background: #5A2E1E !important;
				}
				@media (max-width: 900px) {
					.fraud-grid { flex-direction: column !important; }
					.fraud-tiles { grid-template-columns: 1fr 1fr !important; }
				}
				@media (max-width: 520px) {
					.fraud-tiles { grid-template-columns: 1fr !important; }
				}
			`}</style>

      <section
        style={{
          width: "100%",
          boxSizing: "border-box",
          padding:
            "clamp(32px, 4vw, 60px) clamp(20px, 5.56vw, 80px)",
        }}
      >
        <div
          style={{
            maxWidth: "1280px",
            margin: "0 auto",
            position: "relative",
            background: "#2A1810",
            borderRadius: "24px",
            padding: "clamp(32px, 4vw, 64px)",
            overflow: "hidden",
            boxSizing: "border-box",
          }}
        >
          {/* Subtle dot texture overlay */}
          <svg
            aria-hidden="true"
            width="100%"
            height="100%"
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              opacity: 0.06,
              pointerEvents: "none",
            }}
          >
            <defs>
              <pattern
                id="fraud-pattern"
                x="0"
                y="0"
                width="32"
                height="32"
                patternUnits="userSpaceOnUse"
              >
                <circle cx="16" cy="16" r="1" fill="#F4D2A8" />
              </pattern>
            </defs>
            <rect width="100%" height="100%" fill="url(#fraud-pattern)" />
          </svg>

          <div className="fraud-grid" style={{ position: "relative" }}>
            {/* Left — Headline + body */}
            <div className="fraud-left">
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "clamp(16px, 1.6vw, 24px)",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                  }}
                >
                  <span
                    style={{
                      width: "8px",
                      height: "8px",
                      borderRadius: "50%",
                      backgroundColor: "#86C7A8",
                      display: "inline-block",
                    }}
                  />
                  <span
                    style={{
                      ...geistMono,
                      fontWeight: 500,
                      fontSize: "12px",
                      lineHeight: 1,
                      letterSpacing: "0.1em",
                      color: "#86C7A8",
                      textTransform: "uppercase",
                    }}
                  >
                    Security alert
                  </span>
                </div>

                <h2
                  style={{
                    ...instrumentSerif,
                    fontWeight: 400,
                    fontSize: "clamp(28px, 3.6vw, 52px)",
                    lineHeight: 1.1,
                    letterSpacing: "-0.01em",
                    color: "#FFFFFF",
                    margin: 0,
                    maxWidth: "min(440px, 100%)",
                  }}
                >
                  Report suspected fraud immediately.
                </h2>

                <p
                  style={{
                    ...raleway,
                    fontWeight: 400,
                    fontSize: "clamp(14px, 1.1vw, 16px)",
                    lineHeight: 1.7,
                    color: "rgba(255,255,255,0.75)",
                    margin: 0,
                    maxWidth: "min(460px, 100%)",
                  }}
                >
                  If you notice suspicious activity on your account, unexpected
                  debits, strange login alerts, or anyone asking for your PIN
                  or OTP, contact us immediately. We will never ask for your
                  PIN, password, or OTP.
                </p>
              </div>
            </div>

            {/* Right — Contact tiles */}
            <div className="fraud-right">
              <div className="fraud-tiles">
                {CONTACT_TILES?.map((t) => (
                  <ContactTile
                    key={t?.eyebrow}
                    {...t}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default FraudReport;
